import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";

const NAV_ITEMS = [
  { label: "Browse cars", to: "/browse" },
  { label: "How it works", to: "/#how" },
  { label: "Reviews", to: "/#reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const solid = scrolled || location.pathname !== "/" || open;
  const overImage = !solid;
  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Account";
  const initial = displayName.charAt(0).toUpperCase();
  const themeIcon = theme === "dark" ? "ri-sun-line" : "ri-moon-line";

  async function handleSignOut() {
    await signOut();
    setMenuOpen(false);
    navigate("/");
  }

  const toggleButtonClass = `flex h-10 w-10 items-center justify-center rounded-md border text-lg transition-colors ${
    overImage
      ? "border-white/25 text-white hover:bg-white/10"
      : "border-background-200 text-foreground-900 hover:border-primary-500/60"
  }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "border-b border-background-200/70 bg-background-50/90 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
            <i className="ri-roadster-fill"></i>
          </span>
          <span className={`font-heading text-xl font-semibold tracking-tight ${overImage ? "text-white" : "text-foreground-950"}`}>
            Gari
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                overImage ? "text-white/85 hover:text-white" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <button type="button" onClick={toggleTheme} aria-label="Toggle colour theme" className={toggleButtonClass}>
            <i className={themeIcon}></i>
          </button>

          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex items-center gap-2.5 rounded-full border border-background-200 bg-background-100 py-1.5 pl-1.5 pr-3.5 transition-colors hover:border-primary-500/60"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-semibold text-background-50">
                  {initial}
                </span>
                <span className="max-w-[120px] truncate text-sm text-foreground-900">{displayName}</span>
                <i className={`text-foreground-500 ${menuOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-xl border border-background-200 bg-background-100 py-1.5">
                  <div className="border-b border-background-200/70 px-4 py-3">
                    <p className="truncate text-sm font-medium text-foreground-950">{displayName}</p>
                    <p className="truncate text-xs text-foreground-500">{user.email}</p>
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary-500/12 px-2.5 py-1 text-xs font-medium capitalize text-primary-400">
                      <i className={profile?.role === "host" ? "ri-hand-coin-line" : "ri-steering-2-line"}></i>
                      {profile?.role || "rider"}
                    </span>
                  </div>
                  <Link
                    to="/trips"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
                  >
                    <i className="ri-roadster-line"></i> My trips
                  </Link>
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
                  >
                    <i className="ri-dashboard-line"></i> Host dashboard
                  </Link>
                  <Link
                    to="/host"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
                  >
                    <i className="ri-add-circle-line"></i> List your car
                  </Link>
                  <Link
                    to="/browse"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
                  >
                    <i className="ri-search-eye-line"></i> Find a car
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-foreground-950"
                  >
                    <i className="ri-user-settings-line"></i> Account settings
                  </Link>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-secondary-300 transition-colors hover:bg-background-200/60"
                  >
                    <i className="ri-logout-box-r-line"></i> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/auth"
                className={`whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  overImage ? "text-white/90 hover:text-white" : "text-foreground-800 hover:text-foreground-950"
                }`}
              >
                Sign in
              </Link>
              <Link
                to="/auth?redirect=/host"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-circle-line"></i> Become a host
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button type="button" onClick={toggleTheme} aria-label="Toggle colour theme" className={toggleButtonClass}>
            <i className={themeIcon}></i>
          </button>
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setOpen((prev) => !prev)}
            className={toggleButtonClass}
          >
            <i className={open ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-background-200/70 bg-background-50/95 px-4 pb-5 pt-3 backdrop-blur-xl md:hidden">
          {user && (
            <div className="mb-3 flex items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-3 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-semibold text-background-50">
                {initial}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground-950">{displayName}</p>
                <p className="truncate text-xs capitalize text-foreground-500">{profile?.role || "rider"}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                {item.label}
              </Link>
            ))}
          </div>

          {user ? (
            <>
              <div className="mt-3 flex flex-col">
                <Link
                  to="/trips"
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-roadster-line"></i> My trips
                </Link>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-dashboard-line"></i> Host dashboard
                </Link>
                <Link
                  to="/host"
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-add-circle-line"></i> List your car
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-user-settings-line"></i> Account settings
                </Link>
                <Link
                  to="/policies"
                  className="flex items-center gap-2.5 rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                >
                  <i className="ri-file-shield-2-line"></i> Policies &amp; insurance
                </Link>
              </div>
              <button
                type="button"
                onClick={handleSignOut}
                className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm font-medium text-secondary-300"
              >
                <i className="ri-logout-box-r-line"></i> Sign out
              </button>
            </>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Link
                to="/auth"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-900"
              >
                <i className="ri-login-circle-line"></i> Sign in
              </Link>
              <Link
                to="/auth?redirect=/host"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50"
              >
                <i className="ri-add-circle-line"></i> Host
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}